import LoginIcon from "@mui/icons-material/Login";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { signInWithEmailAndPassword, UserCredential } from "firebase/auth";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { z } from "zod";
import CardWithIcon from "../../components/cards/CardWithIcon";
import PasswordInput from "../../components/inputs/PasswordInput";
import TextInput from "../../components/inputs/TextInput";
import { useApi } from "../../contexts/ApiContext";
import { useAppContext } from "../../contexts/AppContext";
import { useError } from "../../contexts/ErrorContext";
import { auth } from "../../firebase/firebase";
import Translations from "../../lang/en/pages.json";
import Minimal from "../../layouts/Minimal";
import Caching from "../../CacheManager";

const loginSchema = z.object({
  email: z.string().email(Translations.pages.auth_page.errors.empty_email),
  password: z
    .string()
    .min(1, Translations.pages.auth_page.errors.empty_password),
});

const StyledIcon = styled(LoginIcon)(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  padding: 8,
  borderRadius: 8,
  color: "white",
  boxSizing: "content-box",
}));

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { showErrors, showError, showSuccess } = useError();
  const { isLoading, setIsLoading } = useAppContext();
  const { client } = useApi();

  const navigate = useNavigate();

  useEffect(() => {
    Caching.clear();
  }, []);

  const handleSubmit = async () => {
    try {
      loginSchema.parse({ email, password });

      const userCred: UserCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      // const idToken = await userCred.user.getIdToken();

      setTimeout(() => {
        window.location.href = "/welcome";
      }, 500);
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors = error.flatten().fieldErrors;
        showErrors([
          fieldErrors.email?.[0] ?? "",
          fieldErrors.password?.[0] ?? "",
        ]);
      } else {
        // Firebase auth errors or other issues
        showError(Translations.pages.auth_page.errors.invlid_login);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Minimal>
      <CardWithIcon
        width={500}
        icon={<StyledIcon />}
        disabled={isLoading}
        buttonText={
          isLoading ? (
            <CircularProgress size={20} />
          ) : (
            Translations.pages.auth_page.login.title
          )
        }
        onButtonClick={handleSubmit}
      >
        <Stack spacing={8} textAlign={"center"}>
          <Stack spacing={2} textAlign={"center"}>
            <Typography
              sx={{ color: "#266A4A" }}
              fontWeight={"bold"}
              variant="h5"
            >
              {Translations.pages.auth_page.login.welcome}
            </Typography>
            <Typography
              color={"textPrimary"}
              fontWeight={"light"}
              variant="caption"
            >
              {Translations.pages.auth_page.login.description}
            </Typography>
          </Stack>
          <Stack spacing={4}>
            <TextInput
              label={Translations.pages.auth_page.login.email.label}
              placeholder={Translations.pages.auth_page.login.email.label}
              value={email}
              onChange={(value: string | number) => setEmail(value as string)}
              helperText={Translations.pages.auth_page.login.email.helper_text}
            />
            <PasswordInput
              label={Translations.pages.auth_page.login.password.label}
              placeholder={Translations.pages.auth_page.login.password.label}
              value={password}
              onChange={(value: string | number) =>
                setPassword(value as string)
              }
              helperText={
                Translations.pages.auth_page.login.password.helper_text
              }
            />
          </Stack>
        </Stack>
      </CardWithIcon>
    </Minimal>
  );
};

export default Login;
